import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environment';
import { HttpListResponse } from '@ess-front/shared/models';
import { StoryAuthorBE } from '@app/utils/models/story-author-be.model';
import { StoryAuthor } from '@app/utils/models/story-author.model';
import { StoryBE } from '@app/utils/models/story-be.model';
import { StoryDetailBE } from '@app/utils/models/story-detail-be.model';
import { StoryDetail } from '@app/utils/models/story-detail.model';
import { StoryFilter } from '@app/utils/models/story-filter.model';
import { Story } from '@app/utils/models/story.model';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StoriesApiService {
  private readonly apiURL = `${environment.apiEnv}api/stories/`;
  private readonly STORIES_LIMIT = 30;

  // DI
  private readonly httpClient = inject(HttpClient);

  bookmarkStory$(slug: string): Observable<void> {
    return this.httpClient.post<void>(`${this.apiURL}${slug}/bookmark`, null);
  }

  getStoriesByFilter$(storyFilter: StoryFilter): Observable<Story[]> {
    const params = new HttpParams().set('limit', this.STORIES_LIMIT).set('offset', 0).set('type', storyFilter);
    return this.httpClient
      .get<HttpListResponse<StoryBE>>(this.apiURL, { params })
      .pipe(map(response => response.results.map(storyBE => this.transformIntoStory(storyBE))));
  }

  getStoryDetail$(slug: string): Observable<StoryDetail> {
    return this.httpClient
      .get<StoryDetailBE>(`${this.apiURL}${slug}`)
      .pipe(map(storyDetailBE => this.transformIntoStoryDetail(storyDetailBE)));
  }

  unbookmarkStory$(slug: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.apiURL}${slug}/bookmark`);
  }

  private transformIntoStory(storyBE: StoryBE): Story {
    return {
      ...this.transformIntoStoryBase(storyBE),
      featuredImage: storyBE.featured_image,
      id: storyBE.id,
    };
  }

  // Fields shared by the list (StoryBE) and the detail (StoryDetailBE), which has images instead of an id and featured image
  private transformIntoStoryBase(storyBE: Omit<StoryBE, 'featured_image' | 'id'>): Omit<Story, 'featuredImage' | 'id'> {
    return {
      author: this.transformIntoStoryAuthor(storyBE.author),
      city: storyBE.city,
      description: storyBE.description,
      durationTag: storyBE.duration_tag,
      endDate: storyBE.end_date ?? null,
      hasExclusiveBenefits: storyBE.has_exclusive_benefits ?? false,
      isBookmarked: storyBE.is_bookmarked,
      isEditorPick: storyBE.is_editor_pick ?? false,
      isNewOpening: storyBE.is_new_opening ?? false,
      slug: storyBE.slug,
      startDate: storyBE.start_date ?? null,
      subcategory: storyBE.subcategory,
      title: storyBE.title,
      type: storyBE.type,
    };
  }

  // The BE may send either name alone (the Essentialist byline is a first name only), and a nameless author gets no byline.
  private transformIntoStoryAuthor(storyAuthorBE: StoryAuthorBE): StoryAuthor {
    return {
      avatar: storyAuthorBE.avatar,
      bio: storyAuthorBE.bio,
      fullName: [storyAuthorBE.first_name, storyAuthorBE.last_name].filter(Boolean).join(' ') || null,
    };
  }

  private transformIntoStoryDetail(storyDetailBE: StoryDetailBE): StoryDetail {
    return {
      ...this.transformIntoStoryBase(storyDetailBE),
      benefits: storyDetailBE.benefits,
      collapsibleContent: storyDetailBE.collapsible_content,
      content: storyDetailBE.content,
      hotels: storyDetailBE.hotels.map(({ featured_image, quote, title }) => ({
        featuredImage: featured_image,
        quote,
        title,
      })),
      images: storyDetailBE.images,
      subtitle: storyDetailBE.subtitle,
    };
  }
}
